<template>
    <div class="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div class="container py-5">
            <div class="text-center mb-5">
                <h1 class="display-4 fw-bold mb-3">Choose Your Membership</h1>
                <p class="lead text-muted">Select the membership plan that best fits your needs</p>
            </div>

            <div class="row g-4">
                <div v-for="membership in membershipTypes" :key="membership.type" class="col-md-4">
                    <div class="card h-100 shadow-sm border-0 hover-shadow transition">
                        <div class="card-body d-flex flex-column p-4">
                            <!-- Membership Badge -->
                            <div class="mb-3">
                                <span class="badge fs-6 px-3 py-2" :class="{
                                    'bg-success': membership.type === 'student',
                                    'bg-primary': membership.type === 'individual',
                                    'bg-purple': membership.type === 'corporate'
                                }">
                                    {{ membership.type.toUpperCase() }}
                                </span>
                            </div>

                            <!-- Membership Name -->
                            <h3 class="card-title fw-bold mb-2">{{ membership.name }}</h3>

                            <!-- Description -->
                            <p class="text-muted mb-4">{{ membership.description }}</p>

                            <!-- Price -->
                            <div class="mb-4">
                                <h2 class="display-5 fw-bold mb-0">
                                    {{ membership.currency_symbol }}{{ membership.price.toFixed(2) }}
                                </h2>
                                <small class="text-muted">{{ membership.currency }} per {{ membership.duration }}</small>
                            </div>

                            <!-- Features List -->
                            <ul class="list-unstyled mb-4 flex-grow-1">
                                <li v-for="(feature, index) in membership.features" :key="index" class="mb-2">
                                    <i class="bi bi-check-circle-fill text-success me-2"></i>
                                    <span>{{ feature }}</span>
                                </li>
                            </ul>

                            <!-- Payment Options -->
                            <div class="d-grid gap-2">

                                <!-- Stripe Button -->
                                <button v-if="enabledGateways.includes('stripe')" @click="subscribe(membership.type, 'stripe')" class="btn btn-lg" :class="{
                                    'btn-success': membership.type === 'student',
                                    'btn-primary': membership.type === 'individual',
                                    'btn-purple': membership.type === 'corporate'
                                }" :disabled="processing">
                                    <span v-if="processing && selectedTier === membership.type && paymentMethod === 'stripe'">
                                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Processing...
                                    </span>
                                    <span v-else>
                                        <i class="bi bi-credit-card me-2"></i>
                                        Pay with Stripe
                                    </span>
                                </button>

                                <!-- PayPal Button -->
                                <button v-if="enabledGateways.includes('paypal')" @click="subscribe(membership.type, 'paypal')" class="btn btn-lg btn-warning" :disabled="processing">
                                    <span v-if="processing && selectedTier === membership.type && paymentMethod === 'paypal'">
                                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Processing...
                                    </span>
                                    <span v-else>
                                        <i class="bi bi-paypal me-2"></i>
                                        Pay with PayPal
                                    </span>
                                </button>

                                <!-- PayStack Button -->
                                <button v-if="enabledGateways.includes('paystack')" @click="subscribe(membership.type, 'paystack')" class="btn btn-lg btn-info text-white" :disabled="processing">
                                    <span v-if="processing && selectedTier === membership.type && paymentMethod === 'paystack'">
                                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Processing...
                                    </span>
                                    <span v-else>
                                        <i class="bi bi-wallet2 me-2"></i>
                                        Pay with PayStack
                                    </span>
                                </button>

                            </div>
                        </div>
                    </div>
                </div>
            </div>


        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { router } from '@inertiajs/vue3';

const props = defineProps({
    user: Object,
    membershipTypes: Array,
    enabledGateways: {
        type: Array,
        default: () => ['stripe'] // Default to Stripe if not provided
    },
});

const processing = ref(false);
const selectedTier = ref(null);
const paymentMethod = ref(null);

const subscribe = (tier, method) => {
    if (processing.value) return;

    processing.value = true;
    selectedTier.value = tier;
    paymentMethod.value = method;

    // Handle different payment methods
    if (method === 'stripe') {
        // Use Stripe Checkout
        router.post('/stripe/create-checkout-session',
            { tier },
            {
                preserveState: true,
                preserveScroll: true,
                onError: (errors) => {
                    processing.value = false;
                    selectedTier.value = null;
                    paymentMethod.value = null;
                    console.error('Payment error:', errors);
                }
            }
        );
    } else if (method === 'paypal') {
        // PayPal integration
        router.post('/paypal/create-payment',
            { tier },
            {
                preserveState: true,
                preserveScroll: true,
                onError: (errors) => {
                    processing.value = false;
                    selectedTier.value = null;
                    paymentMethod.value = null;
                    console.error('PayPal payment error:', errors);
                }
            }
        );
    } else if (method === 'paystack') {
        // PayStack integration
        router.post('/paystack/create-payment',
            { tier },
            {
                preserveState: true,
                preserveScroll: true,
                onError: (errors) => {
                    processing.value = false;
                    selectedTier.value = null;
                    paymentMethod.value = null;
                    console.error('PayStack payment error:', errors);
                }
            }
        );
    }
};
</script>

<style scoped>
.hover-shadow {
    transition: all 0.3s ease;
}

.hover-shadow:hover {
    transform: translateY(-5px);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.transition {
    transition: all 0.3s ease;
}

.btn-purple {
    background-color: #6f42c1;
    border-color: #6f42c1;
    color: white;
}

.btn-purple:hover {
    background-color: #5a32a3;
    border-color: #5a32a3;
    color: white;
}

.bg-purple {
    background-color: #6f42c1 !important;
}

.card {
    border-radius: 1rem;
}

.badge {
    border-radius: 0.5rem;
}
</style>
